import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generatePDF, generateWord } from '../utils/exportHelpers';

const { FiCalendar, FiTarget, FiRefreshCw, FiDownload, FiMail, FiCheckCircle } = FiIcons;

const ActionPlanResults = ({ responses, plan, onRestart }) => {
  const handleExportPDF = async () => {
    const content = {
      title: '7-Day Faithful Action Plan',
      track: plan.track,
      subTrack: plan.subTrack,
      timeBlock: plan.timeBlock,
      primaryOutcome: responses['primary-outcome'],
      secondaryOutcome: responses['secondary-outcome'],
      days: plan.days,
      date: new Date().toLocaleDateString()
    };

    await generatePDF(content, '7-day-action-plan.pdf');
  };

  const handleExportWord = async () => {
    const content = {
      title: '7-Day Faithful Action Plan',
      track: plan.track,
      subTrack: plan.subTrack,
      timeBlock: plan.timeBlock,
      primaryOutcome: responses['primary-outcome'],
      secondaryOutcome: responses['secondary-outcome'],
      days: plan.days,
      date: new Date().toLocaleDateString()
    };

    await generateWord(content, '7-day-action-plan.docx');
  };

  const handleEmailResults = () => {
    const subject = encodeURIComponent('My 7-Day Faithful Action Plan');
    const body = encodeURIComponent(`
I just created my 7-Day Faithful Action Plan!

Track: ${plan.track}
Time Block: ${plan.timeBlock} minutes daily
Primary Focus: ${responses['primary-outcome']}

This plan will help me make meaningful progress on my manuscript over the next week. Each day includes focused writing time, faith practices, and accountability support.

Key milestones include ${plan.days[0].coreTask.task.toLowerCase()} and ${plan.days[3].coreTask.task.toLowerCase()}.

I'm excited to see what God will help me accomplish!
    `);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4"
        >
          <h1 className="text-4xl font-serif text-gray-900">
            Your 7-Day Faithful Action Plan
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-medium">
              {plan.track}
            </div>
            <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-medium">
              {plan.timeBlock} minutes daily
            </div>
            <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-medium">
              {responses['primary-outcome']}
            </div>
          </div>
        </motion.div>

        {/* Day Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {plan.days.map((day, index) => (
            <motion.div
              key={day.day}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`${day.isGraceDay ? 'bg-gray-50 border-gray-300' : 'bg-white'} rounded-xl shadow-lg p-6 border-2 relative overflow-hidden`}
            >
              {day.isGraceDay && (
                <div className="absolute top-2 right-2 bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
                  Grace Day
                </div>
              )}
              
              <div className="space-y-4">
                {/* Day Header */}
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Day {day.day}</h3>
                </div>

                {day.energyNote && (
                  <p className="text-xs text-gray-500 italic">{day.energyNote}</p>
                )}

                {/* Warm-up */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-purple-600">Warm-up</p>
                  <p className="text-sm text-gray-700">{day.warmUp}</p>
                </div>

                {/* Core Task */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-blue-600">Core Task</p>
                  <p className="text-sm text-gray-700">{day.coreTask.task}</p>
                  <p className="text-xs text-gray-500">({day.coreTask.minutes} minutes)</p>
                </div>

                {/* Wrap-up */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-600">Wrap-up</p>
                  <p className="text-sm text-gray-700">{day.wrapUp}</p>
                </div>

                {/* Faith Element */}
                <div className="bg-purple-50 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-purple-700">Faith Focus</p>
                  <p className="text-xs text-gray-700 italic">{day.faithElement}</p>
                </div>

                {/* Accountability */}
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                  <span>{day.accountability}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Weekly Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-xl p-8 text-white"
        >
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon={FiTarget} className="text-2xl" />
            <h2 className="text-2xl font-semibold">Weekly Summary</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <h3 className="font-semibold mb-3">3 Key Milestones</h3>
              <ul className="space-y-2 text-sm">
                <li>• {plan.days[0].coreTask.task}</li>
                <li>• {plan.days[3].coreTask.task}</li>
                <li>• {plan.days[6].coreTask.task}</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-3">Fallback Task</h3>
              <p className="text-sm">Light review and reflection (15 minutes)</p>
              <p className="text-xs text-purple-200 mt-2">For days when energy is low or time is tight</p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-3">Success Principle</h3>
              <p className="text-sm">Consistency over intensity. Small, faithful steps lead to significant progress.</p>
            </div>
          </div>

          <div className="bg-white/20 rounded-xl p-4">
            <p className="text-sm leading-relaxed">
              This plan is designed to be flexible and grace-filled. Adjust as needed while maintaining forward momentum. 
              The goal is progress, not perfection. Trust God to work through your faithful efforts.
            </p>
          </div>
        </motion.div>

        {/* Export Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Save Your Action Plan</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleExportPDF}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-all duration-300"
              >
                <SafeIcon icon={FiDownload} className="text-lg" />
                PDF
              </button>
              <button
                onClick={handleExportWord}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-all duration-300"
              >
                <SafeIcon icon={FiDownload} className="text-lg" />
                Word
              </button>
            </div>
            <button
              onClick={handleEmailResults}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-300"
            >
              <SafeIcon icon={FiMail} className="text-lg" />
              Email Plan
            </button>
          </div>
        </motion.div>

        {/* Restart Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <button
            onClick={onRestart}
            className="inline-flex items-center gap-3 px-6 py-3 bg-accent hover:bg-orange-600 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiRefreshCw} className="text-lg" />
            Back to Tools
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ActionPlanResults;